// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import FormControlLabel from "@mui/material/FormControlLabel";
import { SelectChangeEvent } from "@mui/material/Select";
import { useTheme } from "@mui/material/styles";
import DefaultCard from "src/components/shared/layouts/DefaultCard/DefaultCard";
import PatientLanguageDropdown from "src/components/containers/shared/PatientLanguageDropdown/PatientLanguageDropdown";
import ClinicLanguageDropdown from "src/components/containers/shared/ClinicLanguageDropdown/ClinicLanguageDropdown";
import TimezoneDropdown from "src/components/containers/shared/TimezoneDropdown/TimezoneDropdown";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import CustomSwitch from "src/components/shared/inputs/CustomSwitch/CustomSwitch";
import NumberInputField from "src/components/shared/inputs/NumberInputField/NumberInputField";

// Styles
import { UI_SIZES } from "src/constants/uiTokens";
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Types
import { UpdateClinicSettingsType } from "src/services/clinics/types";

// Constants
import { CURRENCY_DATA } from "src/constants/enums";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Utils
import { canUseAnySimulations } from "src/utils/clinic/clinicLicensingHelper";

const MIN_COLOR_LIMIT = 0;
const MAX_COLOR_LIMIT = 20;

type SiteSettingsType = {
  isLoading: boolean;
  isUpdating: boolean;
  currentSettings: UpdateClinicSettingsType;
  handleUpdateSettings: (settings: UpdateClinicSettingsType) => void;
};

const getSwitchLabelSx = (mutedText: string) => ({
  marginRight: 0,
  "& .MuiFormControlLabel-label": {
    fontSize: "14px",
    marginLeft: "0px",
    color: mutedText,
    "&.Mui-disabled": { color: mutedText },
  },
});

const SiteSettings = ({
  isLoading,
  isUpdating,
  currentSettings,
  handleUpdateSettings,
}: SiteSettingsType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();

  const canDisplayLensSim = useMemo(
    () => canUseAnySimulations(clinicInfo?.licensing),
    [clinicInfo?.licensing]
  );

  const currencyOptions = useMemo(
    () =>
      Object.entries(CURRENCY_DATA)
        .map(([_, lang]) => ({
          displayName: `${lang.symbol} - ${lang.label}`,
          value: `${lang.symbol} - ${lang.label}`,
          symbol: lang.symbol,
          code: lang.label,
        }))
        .sort((a, b) => a?.displayName?.localeCompare(b?.displayName)),
    []
  );

  const enablePortalSounds = useMemo(
    () => Boolean(currentSettings?.enablePortalSounds),
    [currentSettings?.enablePortalSounds]
  );

  const enableVRSounds = useMemo(
    () => Boolean(currentSettings?.enableVRExamCompleteNotificationSounds),
    [currentSettings?.enableVRExamCompleteNotificationSounds]
  );

  const handleSetCurrency = useCallback(
    (event: SelectChangeEvent<any>) => {
      const value = currencyOptions.find(
        (option) => option.displayName === event.target.value
      );

      if (value) {
        handleUpdateSettings({
          ...currentSettings,
          currencySymbol: value.symbol,
          currencyCode: value.code,
        });
      }
    },
    [currencyOptions, currentSettings, handleUpdateSettings]
  );

  const handleUpdatePortalSwitch = useCallback(
    (inputValue: boolean) => {
      handleUpdateSettings({
        ...currentSettings,
        enablePortalSounds: inputValue,
      });
    },
    [handleUpdateSettings, currentSettings]
  );

  const handleUpdateVRSwitch = useCallback(
    (inputValue: boolean) => {
      handleUpdateSettings({
        ...currentSettings,
        enableVRExamCompleteNotificationSounds: inputValue,
      });
    },
    [handleUpdateSettings, currentSettings]
  );

  const [timeLimit, setTimeLimit] = useState("");
  const hasInitializedTimeLimit = useRef(false);

  useEffect(() => {
    if (
      !hasInitializedTimeLimit.current &&
      currentSettings?.colorVisionDefaultTimeLimit >= 0
    ) {
      hasInitializedTimeLimit.current = true;
      setTimeLimit(currentSettings.colorVisionDefaultTimeLimit.toString());
    }
  }, [currentSettings]);

  // Save the color vision time limit on blurring the input
  const handleTimeLimitBlur = useCallback(
    (finalValue: string) => {
      // Round the value to enforce integer input
      const roundedValue =
        finalValue === "" || Number.isNaN(Number(finalValue))
          ? MIN_COLOR_LIMIT
          : Math.round(Number(finalValue));

      setTimeLimit(roundedValue.toString());

      // if nothing changed, don't save the value
      if (roundedValue === currentSettings?.colorVisionDefaultTimeLimit) {
        return;
      }

      handleUpdateSettings({
        ...currentSettings,
        colorVisionDefaultTimeLimit: roundedValue,
      });
    },
    [currentSettings, handleUpdateSettings]
  );

  const skeletonInput = (
    <Skeleton
      variant="rounded"
      width="100%"
      height={UI_SIZES.inputHeight}
      sx={{ borderRadius: "4px" }}
    />
  );

  return (
    <Box display="flex" flexWrap="wrap" gap="16px" width="100%">
      <Box sx={{ flex: "1 1 400px", minWidth: 0 }}>
        <DefaultCard>
          <Box display="flex" flexDirection="column" gap="12px">
            {/* Clinic Language */}
            <Box display="flex" flexDirection="column" gap="6px">
              <TitleWithTooltip
                title={t("Clinic Default Language")}
                tooltip={t("The language displayed on this site.")}
                titleStyle={{ fontSize: "14px", fontWeight: 600 }}
              />
              {isLoading ? (
                skeletonInput
              ) : (
                <ClinicLanguageDropdown
                  isDisabled={isUpdating}
                  currentSettings={currentSettings}
                  handleUpdateSettings={handleUpdateSettings}
                />
              )}
            </Box>

            {/* Patient Language */}
            <Box display="flex" flexDirection="column" gap="6px">
              <TitleWithTooltip
                title={t("Patient Default Language")}
                tooltip={t("The language selected by default on new patients.")}
                titleStyle={{ fontSize: "14px", fontWeight: 600 }}
              />
              {isLoading ? (
                skeletonInput
              ) : (
                <PatientLanguageDropdown
                  isDisabled={isUpdating}
                  currentSettings={currentSettings}
                  handleUpdateSettings={handleUpdateSettings}
                />
              )}
            </Box>

            {/* Clinic Timezone */}
            <Box display="flex" flexDirection="column" gap="6px">
              <TitleWithTooltip
                title={t("Clinic Timezone")}
                tooltip={t("The timezone used to display dates on this site.")}
                titleStyle={{ fontSize: "14px", fontWeight: 600 }}
              />
              {isLoading ? skeletonInput : <TimezoneDropdown />}
            </Box>

            {/* Currency Settings */}
            {canDisplayLensSim && (
              <Box display="flex" flexDirection="column" gap="6px">
                <TitleWithTooltip
                  title={t("Clinic Default Currency")}
                  tooltip={t("The currency displayed on this site.")}
                  titleStyle={{ fontSize: "14px", fontWeight: 600 }}
                />
                {isLoading ? (
                  skeletonInput
                ) : (
                  <SelectDropdown
                    id="site-settings-currency-dropdown"
                    disabled={isUpdating}
                    dropdownOptions={currencyOptions}
                    value={`${currentSettings.currencySymbol} - ${currentSettings.currencyCode}`}
                    handleChange={handleSetCurrency}
                  />
                )}
              </Box>
            )}
          </Box>
        </DefaultCard>
      </Box>

      <Box sx={{ flex: "1 1 400px", minWidth: 0 }}>
        <DefaultCard>
          <Box display="flex" flexDirection="column" gap="12px">
            {/* Color Vision Default Time Limit */}
            <Box display="flex" flexDirection="column" gap="6px">
              <TitleWithTooltip
                title={t("Color Vision Default Time Limit")}
                tooltip={t(
                  "The time limit (in minutes) used for color vision exams."
                )}
                titleStyle={{ fontSize: "14px", fontWeight: 600 }}
              />
              {isLoading ? (
                skeletonInput
              ) : (
                <NumberInputField
                  id="site-settings-time-limit-input"
                  disabled={isUpdating}
                  value={timeLimit}
                  onChange={(e) => setTimeLimit(e.target.value ?? "")}
                  onBlur={handleTimeLimitBlur}
                  min={MIN_COLOR_LIMIT}
                  max={MAX_COLOR_LIMIT}
                  placeholder={t("Enter a value")}
                />
              )}
            </Box>

            {/* Exam Completion Audio */}
            <Box display="flex" flexDirection="column" gap="6px">
              <TitleWithTooltip
                title={t("Exam Completion Audio")}
                tooltip={t(
                  "Plays a sound effect on the dashboard and patient pages whenever an exam is completed."
                )}
                titleStyle={{ fontSize: "14px", fontWeight: 600 }}
              />
              <FormControlLabel
                sx={getSwitchLabelSx(mutedText)}
                control={
                  <CustomSwitch
                    disabled={isLoading || isUpdating}
                    checked={enablePortalSounds}
                    onChange={(e) => handleUpdatePortalSwitch(e.target.checked)}
                    sx={{
                      ...(theme.palette.mode === "dark" && {
                        "& .MuiSwitch-track": {
                          backgroundColor: "#202338",
                        },
                        "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track":
                          {
                            backgroundColor: "#20233843",
                          },
                      }),
                    }}
                  />
                }
                label={t("Enable exam completion audio")}
              />
            </Box>

            {/* VR Completion Audio */}
            <Box display="flex" flexDirection="column" gap="6px">
              <TitleWithTooltip
                title={t("VR Completion Audio")}
                tooltip={t(
                  "Plays a sound effect on the VR headset whenever an exam is completed."
                )}
                titleStyle={{ fontSize: "14px", fontWeight: 600 }}
              />
              <FormControlLabel
                sx={getSwitchLabelSx(mutedText)}
                control={
                  <CustomSwitch
                    disabled={isLoading || isUpdating}
                    checked={enableVRSounds}
                    onChange={(e) => handleUpdateVRSwitch(e.target.checked)}
                    sx={{
                      ...(theme.palette.mode === "dark" && {
                        "& .MuiSwitch-track": {
                          backgroundColor: "#202338",
                        },
                        "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track":
                          {
                            backgroundColor: "#20233843",
                          },
                      }),
                    }}
                  />
                }
                label={t("Enable VR completion audio")}
              />
            </Box>
          </Box>
        </DefaultCard>
      </Box>
    </Box>
  );
};

export default SiteSettings;
